import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "urql";
import {
  GetNewsFeedDynamicFilterQuery,
  type NewsFeedDynamicSidebarResult,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { normalizeDynamicFilterItems } from "@/store/news-feed/filters/dynamic-filters.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";

/**
 * Hook for fetching and managing dynamic filter data for a specific category.
 *
 * Fetches dynamic filter items (like authors, publishers, topics) from the GraphQL API
 * and manages their state in the combined news feed filters store. Handles loading states,
 * error states, and provides refetch capabilities.
 *
 * @param category - The dynamic filter category to fetch (e.g., "AUTHORS", "PUBLISHERS", "TOPICS")
 * @param enabled - Optional flag to control whether the query should run (default: true)
 * @returns Object containing filter items, loading states, error state, and refetch function
 *
 * @example
 * ```typescript
 * const AuthorsFilter = () => {
 *   const { items: authors, isLoading, error, refetch } = useDynamicFilters("AUTHORS");
 *
 *   if (isLoading) return <div>Loading authors...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       {authors.map(author => (
 *         <label key={author.id}>
 *           <input type="checkbox" />
 *           {author.label} ({author.total})
 *         </label>
 *       ))}
 *     </div>
 *   );
 * };
 * ```
 */
// Shared ref to track the last feedId that triggered a clear
// This ensures we only clear all dynamic filters once per feedId change,
// even when multiple useDynamicFilters hooks are active
const lastClearedFeedIdRef = { current: null as string | null };

// Categories that use cache-and-network instead of network-only.
// These contain items that rarely change within a session (e.g. user-managed lists).
const CACHE_AND_NETWORK_CATEGORIES = new Set([
  "NARRATIVE_CLUSTER",
  "PUBLISHER_MEDIA_LIST_ID",
  "PUBLISHER_MEDIA_LIST_GROUP_ID",
]);

export const useDynamicFilters = (category: string, enabled = true) => {
  const {
    feedId,
    startDate,
    endDate,
    selectedFilters,
    chartSelection,
    feedVolumeStartDate,
    feedVolumeEndDate,
  } = useAnalysisParamsForQueries();
  const previousFeedIdRef = useRef<string | null>(feedId ?? null);

  // Get current state from the combined store
  const categoryData = useNewsFeedFilters((state) =>
    state.dynamicFilters.get(category),
  );

  const {
    setDynamicLoading,
    updateDynamicItems,
    setDynamicError,
    clearAllDynamicFilters,
  } = useNewsFeedFilters();

  const clearSelectedDynamicFilters = useNewsFeedParamsStore(
    (state) => state.clearDynamicFilters,
  );

  // Use cache-and-network for categories whose items rarely change within a session.
  // network-only for all others to ensure counts stay fresh.
  const requestPolicy = CACHE_AND_NETWORK_CATEGORIES.has(category)
    ? "cache-and-network"
    : "network-only";

  // urql handles request cancellation and deduplication; no manual AbortController needed.
  const [result, reexecute] = useQuery<NewsFeedDynamicSidebarResult>({
    query: GetNewsFeedDynamicFilterQuery,
    variables: {
      projectionFilters: {
        ...(feedId ? { feedId } : {}),
        ...(startDate && endDate ? { dateRange: { startDate, endDate } } : {}),
        state: "ADDED",
        ...(chartSelection ? { chartSelection } : {}),
        ...(chartSelection && feedVolumeStartDate && feedVolumeEndDate
          ? {
              chartSelectionDate: {
                startDate: feedVolumeStartDate,
                endDate: feedVolumeEndDate,
              },
            }
          : {}),
        ...((selectedFilters ?? {}) as Record<string, unknown>),
      },
      categories: [category as ShareVoiceSidebarCategory],
    },
    pause: !enabled || !feedId || !startDate || !endDate, // Pause if disabled, missing required params
    requestPolicy,
  });

  // Empty all dynamic filter state when feed changes or when mounting on a feed
  // (on navigate we remount, so previousFeedIdRef is the new feed — we must clear by feedId mismatch with lastClearedFeedIdRef)
  useEffect(() => {
    const feedChanged =
      previousFeedIdRef.current != null &&
      previousFeedIdRef.current !== feedId &&
      feedId != null;

    if (
      feedId != null &&
      startDate &&
      endDate &&
      lastClearedFeedIdRef.current !== feedId
    ) {
      clearAllDynamicFilters();
      clearSelectedDynamicFilters();
      lastClearedFeedIdRef.current = feedId;
    }

    if (feedChanged && feedId != null && startDate && endDate) {
      reexecute({ requestPolicy });
    }

    previousFeedIdRef.current = feedId ?? null;
  }, [
    feedId,
    clearAllDynamicFilters,
    clearSelectedDynamicFilters,
    reexecute,
    startDate,
    endDate,
    category,
  ]);

  // Handle loading state
  useEffect(() => {
    const prev = useNewsFeedFilters
      .getState()
      .dynamicFilters.get(category)?.isLoading;
    if (prev === result.fetching) return;
    setDynamicLoading(category, result.fetching);
  }, [category, result.fetching, setDynamicLoading]);

  // Handle data updates
  useEffect(() => {
    if (result.data?.newsFeedDynamicSidebar) {
      // Filter items to only include those matching the requested category
      // This prevents storing items from other categories (e.g., PUBLISHER items in NARRATIVE_CLUSTER)
      const items = normalizeDynamicFilterItems(
        result.data.newsFeedDynamicSidebar.filter(
          (item) => item.category === category,
        ),
      );
      const prev = useNewsFeedFilters.getState().dynamicFilters.get(category);

      // Compare data to prevent unnecessary updates
      const equal =
        Array.isArray(prev?.items) &&
        prev.items.length === items.length &&
        prev.items.every(
          (p, i) =>
            p.id === items[i]?.id &&
            p.label === items[i]?.label &&
            p.total === items[i]?.total &&
            p.category === items[i]?.category &&
            p.groupId === items[i]?.groupId,
        );

      if (!equal) {
        updateDynamicItems(category, items);
        // Clear any previous errors when new data arrives
        setDynamicError(category, null);
      }
    }
  }, [result.data, category, updateDynamicItems, setDynamicError]);

  // Handle errors
  useEffect(() => {
    const prev = useNewsFeedFilters.getState().dynamicFilters.get(category);

    if (result.error) {
      const errorMessage = result.error.message;

      // Only update if error state has changed to prevent infinite loops
      if (prev?.error !== errorMessage) {
        setDynamicError(category, errorMessage);
      }

      // Ensure loading state is cleared on error
      if (prev?.isLoading) {
        setDynamicLoading(category, false);
      }
    } else if (prev?.error) {
      // Clear error when there's no error
      setDynamicError(category, null);
    }
  }, [result.error, category, setDynamicError, setDynamicLoading]);

  // Refetch function (for when filters change)
  const refetch = useCallback(() => {
    reexecute({ requestPolicy });
  }, [reexecute, requestPolicy]);

  return {
    /** Array of dynamic filter items for the category */
    items: categoryData?.items || [],
    /** Map of filter items keyed by ID for O(1) lookups */
    itemsMap: categoryData?.itemsMap || new Map(),

    /** Whether the initial data fetch is in progress */
    isLoading: categoryData?.isLoading || result.fetching || false,
    /** Whether pagination loading is in progress (future feature) */
    isLoadingMore: categoryData?.isLoadingMore || false,

    /** Error message if data fetching failed, null if no error */
    error: categoryData?.error || null,

    /** Function to manually refetch the data */
    refetch,
  };
};
